import {create} from 'zustand'

type NotificationContextHostStore = {
    hostId: string | null
    /**
     * Search params the claiming surface is deep-linked by. They are cleared in the *same*
     * navigation as the work context when the operator goes back to the notification list —
     * two separate writes cannot do it, because `setSearchParams` builds from the calling
     * render's snapshot, so the later write would restore whatever the earlier one removed.
     */
    hostSearchParams: string[]
    claim: (hostId: string, searchParams?: string[]) => void
    release: (hostId: string) => void
}

/**
 * Arbitrates where the work-context bar renders.
 *
 * An overlaying surface that would otherwise be covered by the wide band can *claim* the host
 * while it is open and render the bar inside itself instead. The claim is always made by the
 * surface, never derived from the notification's type, so a future notification type pointing
 * at an already-claiming surface needs no change here.
 *
 * Not persisted: it only describes what is on screen right now.
 */
export const useNotificationContextHostStore = create<NotificationContextHostStore>()((set, get) => ({
    hostId: null,
    hostSearchParams: [],
    claim: (hostId, searchParams = []) => set({hostId, hostSearchParams: searchParams}),
    // A late unmount must not clear a newer claim, so releasing a stale id is a no-op.
    release: hostId => {
        if (get().hostId === hostId) set({hostId: null, hostSearchParams: []})
    }
}))
